import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, Camera, CheckCircle, X, DeviceTablet } from "@phosphor-icons/react";
import FaceCaptureModal from "@/components/FaceCaptureModal";
import KioskDevicesDialog from "@/components/hr/KioskDevicesDialog";

const EMPTY = {
  name: "", area: "", position: "", phone: "", email: "",
  status: "active",
  hourly_wage: 0, weekly_hours: 48, weekly_salary: 0,
  workdays: ["mon", "tue", "wed", "thu", "fri", "sat"],
  scheduled_entry: "09:00",
  scheduled_lunch_start: "14:00",
  scheduled_lunch_end: "15:00",
  scheduled_exit: "18:00",
  unpaid_lunch_minutes: 30,
  saturday_enabled: false,
  sat_scheduled_entry: "09:00",
  sat_scheduled_lunch_start: "",
  sat_scheduled_lunch_end: "",
  sat_scheduled_exit: "14:00",
  sat_unpaid_lunch_minutes: 0,
  photo_url: "",
  face_descriptor: null,
};

const WEEK_LABELS = [
  { v: "mon", l: "L" }, { v: "tue", l: "M" }, { v: "wed", l: "M" }, { v: "thu", l: "J" },
  { v: "fri", l: "V" }, { v: "sat", l: "S" }, { v: "sun", l: "D" },
];

export default function HrEmployees() {
  const [items, setItems] = useState([]);
  const [filterStatus, setFilterStatus] = useState("active");
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [open, setOpen] = useState(false);
  const [kioskOpen, setKioskOpen] = useState(false);
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/hr/employees").then((r) => setItems(r.data || []));
  useEffect(() => { load(); }, []);

  const filtered = items.filter((e) => filterStatus === "all" || e.status === filterStatus);

  const openNew = () => { setForm(EMPTY); setEditingId(null); setOpen(true); };
  const openEdit = (e) => {
    setForm({ ...EMPTY, ...e, face_descriptor: null });
    setEditingId(e.id);
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = { ...form };
      if (!body.face_descriptor) delete body.face_descriptor;
      if (editingId) {
        await api.put(`/hr/employees/${editingId}`, body);
        toast.success("Empleado actualizado");
      } else {
        await api.post("/hr/employees", body);
        toast.success("Empleado creado");
      }
      await load(); setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al guardar");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!confirm("¿Eliminar empleado? Se perderán sus datos biométricos.")) return;
    try { await api.delete(`/hr/employees/${id}`); await load(); toast.success("Eliminado"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
  };

  const toggleWorkday = (d) => {
    setForm((f) => ({
      ...f,
      workdays: f.workdays.includes(d) ? f.workdays.filter((x) => x !== d) : [...f.workdays, d],
    }));
  };

  return (
    <div className="p-6 sm:p-8 space-y-6">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="font-mono-data text-xs tracking-widest text-zinc-500 mb-1">RH</div>
          <h1 className="font-heading text-4xl font-extrabold tracking-tight">Empleados</h1>
          <p className="text-zinc-500 mt-1 text-sm">{filtered.length} empleado(s) {filterStatus === "active" ? "activos" : filterStatus === "inactive" ? "inactivos" : "totales"}</p>
        </div>
        <div className="flex gap-2">
          <div className="inline-flex border border-zinc-200 rounded overflow-hidden text-xs">
            {[["active", "Activos"], ["inactive", "Inactivos"], ["all", "Todos"]].map(([v, l]) => (
              <button key={v} onClick={() => setFilterStatus(v)} className={`px-3 py-2 ${filterStatus === v ? "bg-zinc-900 text-white" : "bg-white text-zinc-600 hover:bg-zinc-50"}`} data-testid={`emp-filter-${v}`}>{l}</button>
            ))}
          </div>
          <Button variant="outline" onClick={() => setKioskOpen(true)} className="gap-1" data-testid="hr-kiosk-devices-btn">
            <DeviceTablet size={14}/> Kiosco
          </Button>
          <Button onClick={openNew} className="bg-[#002FA7] hover:bg-[#002277] text-white gap-1" data-testid="emp-new-btn">
            <Plus size={14}/> Nuevo empleado
          </Button>
        </div>
      </header>
      <KioskDevicesDialog open={kioskOpen} onOpenChange={setKioskOpen} />

      <div className="border border-zinc-200 bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="emp-table">
          <thead className="bg-zinc-50 text-xs uppercase text-zinc-500">
            <tr>
              <th className="px-4 py-3 text-left">Empleado</th>
              <th className="px-4 py-3 text-left">Área / Puesto</th>
              <th className="px-4 py-3 text-left">Horario</th>
              <th className="px-4 py-3 text-right">$ /hora</th>
              <th className="px-4 py-3 text-right">Hrs sem</th>
              <th className="px-4 py-3 text-right">Salario sem.</th>
              <th className="px-4 py-3 text-center">Biometría</th>
              <th className="px-4 py-3 text-center">Estatus</th>
              <th className="px-4 py-3 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={9} className="px-4 py-12 text-center text-zinc-400 dotted-bg">Sin empleados</td></tr>
            )}
            {filtered.map((e) => (
              <tr key={e.id} className="border-t border-zinc-100" data-testid={`emp-row-${e.id}`}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    {e.photo_url ? (
                      <img src={e.photo_url} alt={e.name} className="w-8 h-8 rounded-full object-cover"/>
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-zinc-200 flex items-center justify-center text-xs font-bold text-zinc-500">
                        {e.name.split(" ").map((s) => s[0]).slice(0, 2).join("")}
                      </div>
                    )}
                    <div>
                      <div className="font-semibold">{e.name}</div>
                      <div className="text-[11px] text-zinc-400">{e.email || e.phone || ""}</div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-zinc-600">{e.area || "—"}<br/><span className="text-[11px] text-zinc-400">{e.position || ""}</span></td>
                <td className="px-4 py-3 text-zinc-600 numeric text-xs">{e.scheduled_entry} → {e.scheduled_exit}</td>
                <td className="px-4 py-3 text-right numeric font-semibold">${Number(e.hourly_wage || 0).toFixed(2)}</td>
                <td className="px-4 py-3 text-right numeric">{e.weekly_hours}</td>
                <td className="px-4 py-3 text-right numeric font-semibold text-[#1E88E5]">
                  {e.weekly_salary ? `MX$${Number(e.weekly_salary).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : <span className="text-zinc-300">—</span>}
                </td>
                <td className="px-4 py-3 text-center">
                  {Array.isArray(e.face_descriptor) && e.face_descriptor.length === 128 ? (
                    <span className="inline-flex items-center gap-1 text-emerald-700 text-xs font-semibold"><CheckCircle size={14} weight="fill"/> Registrado</span>
                  ) : (
                    <span className="text-amber-700 text-xs">Sin biometría</span>
                  )}
                </td>
                <td className="px-4 py-3 text-center">
                  <span className={`inline-block px-2 py-0.5 text-[10px] font-semibold rounded ${e.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-zinc-100 text-zinc-500"}`}>{e.status === "active" ? "Activo" : "Inactivo"}</span>
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex gap-1">
                    <button onClick={() => { openEdit(e); setEnrollOpen(true); }} className="p-1.5 text-zinc-600 hover:text-[#002FA7] hover:bg-zinc-100 rounded" title="Registrar/Actualizar rostro" data-testid={`emp-face-${e.id}`}>
                      <Camera size={14}/>
                    </button>
                    <button onClick={() => openEdit(e)} className="p-1.5 text-zinc-600 hover:text-[#002FA7] hover:bg-zinc-100 rounded" title="Editar" data-testid={`emp-edit-${e.id}`}>
                      <PencilSimple size={14}/>
                    </button>
                    <button onClick={() => remove(e.id)} className="p-1.5 text-zinc-600 hover:text-red-600 hover:bg-zinc-100 rounded" title="Eliminar" data-testid={`emp-del-${e.id}`}>
                      <Trash size={14}/>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Edit / new dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="emp-dialog">
          <DialogHeader><DialogTitle className="font-heading text-2xl">{editingId ? "Editar empleado" : "Nuevo empleado"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><Label className="text-xs uppercase tracking-wider">Nombre completo *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="emp-name"/></div>
              <div><Label className="text-xs uppercase tracking-wider">Área</Label>
                <Input value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })} placeholder="Ventas, Inventario…" data-testid="emp-area"/></div>
              <div><Label className="text-xs uppercase tracking-wider">Puesto</Label>
                <Input value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} data-testid="emp-position"/></div>
              <div><Label className="text-xs uppercase tracking-wider">Teléfono</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="emp-phone"/></div>
              <div><Label className="text-xs uppercase tracking-wider">Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="emp-email"/></div>
              <div><Label className="text-xs uppercase tracking-wider">Estatus</Label>
                <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full border border-zinc-300 rounded px-3 py-2 text-sm" data-testid="emp-status">
                  <option value="active">Activo</option><option value="inactive">Inactivo</option>
                </select></div>
            </div>

            <fieldset className="border border-zinc-200 rounded p-3 space-y-3">
              <legend className="text-xs uppercase tracking-wider text-zinc-500 px-1">Horario L-V y nómina</legend>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div><Label className="text-xs">Entrada</Label><Input type="time" value={form.scheduled_entry} onChange={(e) => setForm({ ...form, scheduled_entry: e.target.value })} data-testid="emp-entry"/></div>
                <div><Label className="text-xs">Inicio comida</Label><Input type="time" value={form.scheduled_lunch_start} onChange={(e) => setForm({ ...form, scheduled_lunch_start: e.target.value })} data-testid="emp-lunch-start"/></div>
                <div><Label className="text-xs">Fin comida</Label><Input type="time" value={form.scheduled_lunch_end} onChange={(e) => setForm({ ...form, scheduled_lunch_end: e.target.value })} data-testid="emp-lunch-end"/></div>
                <div><Label className="text-xs">Salida</Label><Input type="time" value={form.scheduled_exit} onChange={(e) => setForm({ ...form, scheduled_exit: e.target.value })} data-testid="emp-exit"/></div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div><Label className="text-xs">Pago por hora ($)</Label><Input type="number" min="0" step="0.5" value={form.hourly_wage} onChange={(e) => setForm({ ...form, hourly_wage: parseFloat(e.target.value) || 0 })} data-testid="emp-hourly"/></div>
                <div><Label className="text-xs">Horas semana</Label><Input type="number" min="0" step="1" value={form.weekly_hours} onChange={(e) => setForm({ ...form, weekly_hours: parseFloat(e.target.value) || 0 })} data-testid="emp-weekly"/></div>
                <div><Label className="text-xs">Comida no pagada (min)</Label><Input type="number" min="0" step="5" value={form.unpaid_lunch_minutes} onChange={(e) => setForm({ ...form, unpaid_lunch_minutes: parseInt(e.target.value, 10) || 0 })} data-testid="emp-unpaid"/></div>
                <div className="md:col-span-1">
                  <Label className="text-xs">Salario base semanal (MXN)</Label>
                  <Input type="number" min="0" step="100" value={form.weekly_salary} onChange={(e) => setForm({ ...form, weekly_salary: parseFloat(e.target.value) || 0 })} placeholder="ej. 3000" data-testid="emp-weekly-salary"/>
                  <p className="text-[10px] text-zinc-400 mt-0.5">Usado por el módulo de Salarios en Gastos recurrentes</p>
                </div>
              </div>
              <div>
                <Label className="text-xs">Días laborales</Label>
                <div className="flex gap-1 mt-1">
                  {WEEK_LABELS.map(({ v, l }) => (
                    <button key={v} type="button" onClick={() => toggleWorkday(v)} className={`w-9 h-9 rounded text-xs font-bold border ${form.workdays.includes(v) ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-white text-zinc-500 border-zinc-300"}`} data-testid={`emp-day-${v}`}>{l}</button>
                  ))}
                </div>
              </div>
            </fieldset>

            {/* Horario sábado (opcional) */}
            <fieldset className="border border-zinc-200 rounded p-3 space-y-3" style={{ background: form.saturday_enabled ? "rgba(255,192,0,.05)" : "transparent" }}>
              <legend className="text-xs uppercase tracking-wider text-zinc-500 px-1 inline-flex items-center gap-2">
                <input type="checkbox" checked={!!form.saturday_enabled} onChange={(e) => setForm({ ...form, saturday_enabled: e.target.checked })} className="w-3.5 h-3.5 accent-[#FFC000]" data-testid="emp-sat-enabled"/>
                Horario diferente para sábado
              </legend>
              {form.saturday_enabled ? (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div><Label className="text-xs">Entrada sábado</Label><Input type="time" value={form.sat_scheduled_entry} onChange={(e) => setForm({ ...form, sat_scheduled_entry: e.target.value })} data-testid="emp-sat-entry"/></div>
                    <div><Label className="text-xs">Inicio comida</Label><Input type="time" value={form.sat_scheduled_lunch_start} onChange={(e) => setForm({ ...form, sat_scheduled_lunch_start: e.target.value })} data-testid="emp-sat-lunch-start" placeholder="opcional"/></div>
                    <div><Label className="text-xs">Fin comida</Label><Input type="time" value={form.sat_scheduled_lunch_end} onChange={(e) => setForm({ ...form, sat_scheduled_lunch_end: e.target.value })} data-testid="emp-sat-lunch-end" placeholder="opcional"/></div>
                    <div><Label className="text-xs">Salida sábado</Label><Input type="time" value={form.sat_scheduled_exit} onChange={(e) => setForm({ ...form, sat_scheduled_exit: e.target.value })} data-testid="emp-sat-exit"/></div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    <div><Label className="text-xs">Comida no pagada sábado (min)</Label><Input type="number" min="0" step="5" value={form.sat_unpaid_lunch_minutes} onChange={(e) => setForm({ ...form, sat_unpaid_lunch_minutes: parseInt(e.target.value, 10) || 0 })} data-testid="emp-sat-unpaid"/></div>
                  </div>
                  <p className="text-[11px] text-zinc-500">Deja los campos de comida vacíos si el sábado no se otorga hora de comida (se considerará jornada continua).</p>
                </>
              ) : (
                <p className="text-[11px] text-zinc-400 italic">Sábado usa el mismo horario que L-V. Activa el checkbox para definir uno distinto.</p>
              )}
            </fieldset>

            <FaceEnrollment
              onCaptured={({ descriptor, photo }) => setForm((f) => ({ ...f, face_descriptor: descriptor, photo_url: photo }))}
              currentPhoto={form.photo_url}
              alreadyRegistered={editingId && items.find((x) => x.id === editingId)?.face_descriptor?.length === 128}
              autoOpen={enrollOpen}
              onAutoOpenConsumed={() => setEnrollOpen(false)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving || !form.name.trim()} className="bg-[#002FA7] text-white" data-testid="emp-save">
              {saving ? "Guardando…" : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── Face enrollment widget — usa el modal de captura ──────────────────
function FaceEnrollment({ onCaptured, currentPhoto, alreadyRegistered, autoOpen, onAutoOpenConsumed }) {
  const [open, setOpen] = useState(false);
  const [previewPhoto, setPreviewPhoto] = useState(null);

  useEffect(() => {
    if (autoOpen) { setOpen(true); onAutoOpenConsumed && onAutoOpenConsumed(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpen]);

  const showPhoto = previewPhoto || currentPhoto;

  return (
    <fieldset className="border border-zinc-200 rounded p-3 space-y-3">
      <legend className="text-xs uppercase tracking-wider text-zinc-500 px-1">Reconocimiento facial</legend>
      <div className="flex items-start gap-4">
        <div className="w-40 h-32 bg-zinc-100 rounded overflow-hidden flex items-center justify-center border border-zinc-200">
          {showPhoto ? (
            <img src={showPhoto} alt="rostro" className="w-full h-full object-cover"/>
          ) : (
            <Camera size={32} className="text-zinc-300" weight="duotone"/>
          )}
        </div>
        <div className="flex-1 space-y-2">
          {alreadyRegistered && !previewPhoto && (
            <div className="text-xs text-emerald-700 inline-flex items-center gap-1">
              <CheckCircle size={14} weight="fill"/> Biometría registrada
            </div>
          )}
          <Button type="button" onClick={() => setOpen(true)} className="bg-[#002FA7] text-white gap-1" data-testid="enroll-start">
            <Camera size={14}/> {alreadyRegistered && !previewPhoto ? "Actualizar rostro" : "Capturar rostro"}
          </Button>
          <p className="text-[11px] text-zinc-400">
            Se abrirá una vista grande con guía visual. La captura es automática cuando tu rostro esté centrado y enfocado — no necesitas presionar nada extra.
          </p>
        </div>
      </div>
      <FaceCaptureModal
        open={open}
        onClose={() => setOpen(false)}
        onCapture={({ descriptor, photo }) => {
          setPreviewPhoto(photo);
          onCaptured({ descriptor, photo });
          toast.success("Rostro capturado ✓");
        }}
        mode="enroll"
      />
    </fieldset>
  );
}
